// One area's block of the Take Off (the template's rows 48–90): its heading
// with the percentages, a row per surface, the extra rows and the total.
// Down time and abseil % are site facts saved on the area (shared with the
// iPad); overtime and foreman %, surface coats/rate/spread, paint systems
// (#412) and leaving things out belong to this option (PRICING.md, SCHEMA.md "Option pricing JSON").
import type { Fields } from '../lib/device-db';
import {
  addPaintSystem,
  removePaintSystem,
  renamePaintSystem,
  setAreaExcluded,
  setAreaShare,
  setConsumablesRate,
  setPaintSystemPaint,
  setPaintSystemSetting,
  setSurfaceExcluded,
  setSurfacePaint,
  setSurfaceSetting,
  surfacePaintStates,
  systemRowId,
  type AreaChoice,
  type OptionScope,
  type PaintChoice,
} from '@higgins/m2q-pricing';
import type { SectionInfo, TickSource } from '@higgins/m2q-pricing';
import type { PricedSection, PricingRates, SectionInput } from '@higgins/m2q-pricing';
import type { HireItemFromApi } from '../lib/types';
import { NumberField } from './number-field';
import { formatMoney, formatPercent, formatQuantity, formatTwo } from './surface-format';
import { WashOnlyBadge } from './area-wash';
import { hoursPricedText } from './hours-priced-text';
import { TakeOffExtras } from './take-off-extras';
import type { JobRowActions } from '../lib/job-prices';
import { TakeOffSiteNeeds } from './take-off-site-needs';
import { COLUMN_COUNT, ExtraRow, LeftOutRow, NUM, PINNED_CELL, PINNED_ROW, ShareRow } from './take-off-rows';
import { SurfaceRow, SystemRow } from './take-off-surface-rows';

/** What the section can change: the area record's own fields, or this option's choices. */
export interface SectionEdits {
  areaId: string;
  /** The option being priced, for a line's supplier quote (#654). */
  optionId: string;
  onArea: (fields: Fields) => void;
  onScope: (change: (scope: OptionScope) => OptionScope) => void | Promise<void>;
  /** The measure's job prices for hire and materials (#413). */
  job: JobRowActions;
}

function Percent({ label, value, onChange }: { label: string; value: number | null | undefined; onChange: (v: number | null) => void }) {
  return (
    <label className="flex items-center gap-1 text-xs text-slate-600">
      {label}
      <NumberField compact value={value} placeholder="0" onChange={onChange} label={`${label} %`} className="w-12" />%
    </label>
  );
}

interface HeadingProps {
  index: number;
  info: SectionInfo;
  edits: SectionEdits;
  area: { downtime: number | null; abseil: number | null };
  choice: AreaChoice | undefined;
}

function SectionHeading({ index, info, edits, area, choice }: HeadingProps) {
  const { areaId, onArea, onScope } = edits;
  return (
    <tr className="bg-slate-50">
      <td colSpan={COLUMN_COUNT} className="px-2 py-1.5">
        <div className={`${PINNED_ROW} flex flex-wrap items-center gap-x-4 gap-y-1`}>
          <span>
            <span className="font-semibold text-slate-900">
              {index}. {info.name}
            </span>{' '}
            <span className="text-xs text-slate-500">{[info.side, info.building, `count ${info.count}`].filter(Boolean).join(' · ')}</span>
            {info.washOnly && <WashOnlyBadge />}
          </span>
          <Percent label="Down time" value={area.downtime} onChange={(v) => onArea({ hig_downtimepercent: v })} />
          <Percent label="Abseil" value={area.abseil} onChange={(v) => onArea({ hig_abseilpercent: v })} />
          <Percent label="Overtime" value={choice?.overtime} onChange={(v) => onScope((s) => setAreaShare(s, areaId, 'overtime', v))} />
          <Percent label="Foreman" value={choice?.foreman} onChange={(v) => onScope((s) => setAreaShare(s, areaId, 'foreman', v))} />
          <span className="ml-auto text-xs text-slate-500">Hours priced: {hoursPricedText(info)}</span>
          <button type="button" onClick={() => onScope((s) => setAreaExcluded(s, areaId, true))} className="text-xs text-slate-500 underline hover:text-red-700">
            Leave out
          </button>
        </div>
      </td>
    </tr>
  );
}

/** An area this option leaves out: one line, with the way back. */
export function LeftOutSection({ index, info, edits }: { index: number; info: SectionInfo; edits: SectionEdits }) {
  return (
    <tbody className="border-t-2 border-slate-300">
      <LeftOutRow label={`${index}. ${info.name}`} onPutBack={() => edits.onScope((s) => setAreaExcluded(s, edits.areaId, false))} />
    </tbody>
  );
}

interface Props {
  index: number;
  input: SectionInput;
  priced: PricedSection;
  info: SectionInfo;
  rates: PricingRates;
  area: { downtime: number | null; abseil: number | null; siteReqs: TickSource; wash: { wash: boolean; washOnly: boolean } };
  choice: AreaChoice | undefined;
  edits: SectionEdits;
  /** The option's paint list (the generics until it has its own), for each surface's paint. */
  paints: PaintChoice[];
  genericPaints: PaintChoice[];
  hireItems: readonly HireItemFromApi[];
}

export function TakeOffSection({ index, input, priced, info, rates, area, choice, edits, paints, genericPaints, hireItems }: Props) {
  const wageRate = rates.labour.sell + rates.allowance.sell;
  const surfaceInput = new Map(input.surfaces.map((s) => [s.id, s]));
  const rowHours = new Map(priced.surfaces.map((s) => [s.id, s.hours]));
  const { areaId, onScope } = edits;

  return (
    <tbody className="border-t-2 border-slate-300">
      <SectionHeading index={index} info={info} edits={edits} area={area} choice={choice} />
      {priced.surfaces.map((s) => {
        const row = surfaceInput.get(s.id);
        if (!row) return null;
        const measured = info.measured[s.id] ?? { coats: row.coats, rate: row.rate, spread: row.spread };
        const surfaceId = row.systemOf;
        if (surfaceId) {
          const system = choice?.surfaces?.[surfaceId]?.systems?.find((x) => systemRowId(surfaceId, x.id) === s.id);
          if (!system) return null;
          return (
            <SystemRow
              key={s.id}
              priced={s}
              input={row}
              system={system}
              measured={measured}
              paints={paints}
              onPaint={(key) => onScope((scope) => setPaintSystemPaint(scope, genericPaints, areaId, surfaceId, system.id, key))}
              onSetting={(setting, value) => onScope((scope) => setPaintSystemSetting(scope, areaId, surfaceId, system.id, setting, value))}
              onRename={(name) => onScope((scope) => renamePaintSystem(scope, areaId, surfaceId, system.id, name))}
              onRemove={() => onScope((scope) => removePaintSystem(scope, areaId, surfaceId, system.id))}
            />
          );
        }
        return (
          <SurfaceRow
            key={s.id}
            priced={s}
            input={row}
            measured={measured}
            choice={choice?.surfaces?.[s.id]}
            paints={paints}
            paintFlag={info.paintFlags[s.id]}
            states={surfacePaintStates(s.id, s.id in info.paintFlags, choice?.surfaces?.[s.id], (rowId) => rowHours.get(rowId) ?? 0)}
            onPaint={(key) => onScope((scope) => setSurfacePaint(scope, genericPaints, areaId, s.id, key))}
            onSetting={(setting, value) => onScope((scope) => setSurfaceSetting(scope, areaId, s.id, setting, value))}
            onLeaveOut={() => onScope((scope) => setSurfaceExcluded(scope, areaId, s.id, true))}
            onAddSystem={() => onScope((scope) => addPaintSystem(scope, areaId, s.id, ''))}
          />
        );
      })}
      {info.excludedSurfaces.map((s) => (
        <LeftOutRow key={s.id} label={s.name} onPutBack={() => onScope((scope) => setSurfaceExcluded(scope, areaId, s.id, false))} />
      ))}
      {priced.approvedAdjustmentHours !== 0 && (
        <ExtraRow label="Approved hours" detail="replace the surfaces' hours" hours={priced.approvedAdjustmentHours} sell={priced.approvedAdjustmentHours * wageRate} />
      )}
      <ExtraRow label="Down time|Ladder footing|Masking" detail={`${formatPercent(input.downtime ?? 0)} of hours`} hours={priced.downtimeHours} sell={priced.downtimeHours * wageRate} />
      <ExtraRow
        label="Consumables"
        detail={
          <>
            {formatTwo(priced.litres)} L × $
            <NumberField compact value={choice?.consumablesRate ?? null} placeholder={formatTwo(rates.consumablesPerLitre)} onChange={(v) => onScope((s) => setConsumablesRate(s, areaId, v))} label="Consumables $/L" className="w-14" />
            /L
          </>
        }
        sell={priced.consumables}
      />
      <TakeOffSiteNeeds areaId={areaId} siteReqs={area.siteReqs} choice={choice} hireItems={hireItems} onScope={onScope} />
      <TakeOffExtras areaId={areaId} optionId={edits.optionId} choice={choice} info={info} siteWash={area.wash} priced={priced} rates={rates} hireItems={hireItems} job={edits.job} onScope={onScope} />
      <ExtraRow label="Fuel Levy" detail={`${formatTwo(priced.hours)} hrs × ${formatMoney(rates.fuelLevyPerHour)}`} sell={priced.fuelLevy} />
      <ShareRow label="Overtime: {p} of section requiring OT" share={input.overtime ?? 0} hours={priced.overtimeHours} sell={priced.overtimeSell} />
      <ShareRow label="Foreman: {p} of section" share={input.foreman ?? 0} hours={priced.foremanHours} sell={priced.foremanSell} />
      <ShareRow label="Abseil Rate: {p} being abseiled" share={input.abseil ?? 0} hours={priced.abseilHours} sell={priced.abseilSell} />
      <tr className="border-t border-slate-300 bg-slate-50 font-semibold">
        <td className={`${PINNED_CELL} bg-slate-50 px-2 py-1`}>Total</td>
        <td colSpan={2} className={NUM}>
          {formatQuantity(priced.area)} m²
        </td>
        <td colSpan={4} className={NUM}>
          {priced.area > 0 ? `${formatMoney(priced.sell / priced.area)} per m²` : ''}
        </td>
        <td className={NUM}>{formatTwo(priced.hours)}</td>
        <td />
        <td className={NUM}>{formatTwo(priced.litres)}</td>
        <td className={NUM}>{formatMoney(priced.sell)}</td>
        <td colSpan={3} />
      </tr>
    </tbody>
  );
}
